import type { BlockTextureDefinition, BlockTexturePattern } from '../shared/block-textures';

interface Hsl {
  h: number;
  s: number;
  l: number;
}

/** レシピから、ゲーム表示とインベントリプレビューで共用するpixel画像を描く。 */
export function paintBlockTexture(
  canvas: HTMLCanvasElement,
  definition: BlockTextureDefinition
): void {
  const size = definition.resolution;
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) return;
  context.imageSmoothingEnabled = false;

  const base = hexToHsl(definition.color);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const pattern = patternLightness(definition.pattern, x, y, size, definition.seed);
      const noise = noiseLightness(x, y, definition);
      const rgb = hslToRgb({ ...base, l: clamp(base.l + pattern + noise, 0.025, 0.975) });
      context.fillStyle = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
      context.fillRect(x, y, 1, 1);
    }
  }
}

/** CSSの小さなスウォッチへも同じ見た目を適用できるdata URL。 */
export function blockTextureDataUrl(definition: BlockTextureDefinition): string {
  const canvas = document.createElement('canvas');
  paintBlockTexture(canvas, definition);
  return canvas.toDataURL('image/png');
}

function patternLightness(
  pattern: BlockTexturePattern,
  x: number,
  y: number,
  size: number,
  seed: number
): number {
  const edge = Math.min(x, y, size - 1 - x, size - 1 - y);
  const stripe = Math.max(1, Math.round(size / 8));
  const cell = Math.max(2, Math.round(size / 4));

  switch (pattern) {
    case 'plain':
      return 0;
    case 'outline':
      if (edge !== 0) return 0.015;
      return x === size - 1 || y === size - 1 ? 0.07 : -0.16;
    case 'double-outline':
      if (edge === 0) return -0.17;
      if (edge === 1) return 0.075;
      return 0;
    case 'inset': {
      if (edge > 0) return 0.02;
      const topOrLeft = y === 0 || x === 0;
      return topOrLeft ? -0.18 : 0.1;
    }
    case 'horizontal-stripes':
      return Math.floor(y / stripe) % 2 === 0 ? -0.09 : 0.055;
    case 'vertical-stripes':
      return Math.floor(x / stripe) % 2 === 0 ? -0.09 : 0.055;
    case 'diagonal-stripes':
      return Math.floor((x + y) / (stripe * 2)) % 2 === 0 ? -0.1 : 0.055;
    case 'checker':
      return (Math.floor(x / cell) + Math.floor(y / cell)) % 2 === 0 ? -0.095 : 0.055;
    case 'grid':
      return x % cell === 0 || y % cell === 0 ? -0.17 : 0.025;
    case 'bricks': {
      const rowHeight = Math.max(2, Math.round(size / 4));
      const brickWidth = rowHeight * 2;
      const row = Math.floor(y / rowHeight);
      const offset = row % 2 === 0 ? 0 : rowHeight;
      const mortar = y % rowHeight === 0 || (x + offset) % brickWidth === 0;
      return mortar ? -0.19 : row % 2 === 0 ? 0.025 : -0.015;
    }
    case 'planks': {
      const width = Math.max(2, Math.round(size / 4));
      if (x % width === 0) return -0.18;
      const plank = Math.floor(x / width);
      return (hash(plank, 0, seed + 911) - 0.5) * 0.11 + (x % width) * 0.006;
    }
    case 'dots': {
      const center = Math.floor(cell / 2);
      const dx = Math.abs((x % cell) - center);
      const dy = Math.abs((y % cell) - center);
      return dx <= Math.max(0, stripe - 1) && dy <= Math.max(0, stripe - 1) ? -0.18 : 0.025;
    }
  }
}

function noiseLightness(x: number, y: number, definition: BlockTextureDefinition): number {
  if (definition.noiseContrast <= 0) return 0;
  return (
    sampleTextureNoise(x, y, definition.noiseSize, definition.seed) *
    definition.noiseContrast *
    0.16
  );
}

/**
 * 滑らかなValue Noiseを3オクターブ重ねた決定的なfBm。
 * scaleを上げてもpixel数は変えず、隣接pixel間の相関だけを強くする。
 */
export function sampleTextureNoise(x: number, y: number, scale: number, seed: number): number {
  let value = 0;
  let amplitude = 1;
  let amplitudeSum = 0;

  for (let octave = 0; octave < 3; octave++) {
    const octaveScale = Math.max(0.5, scale / 2 ** octave);
    const sampleX = (x + 0.5) / octaveScale;
    const sampleY = (y + 0.5) / octaveScale;
    value += (valueNoise(sampleX, sampleY, seed + octave * 1013) * 2 - 1) * amplitude;
    amplitudeSum += amplitude;
    amplitude *= 0.42;
  }

  return value / amplitudeSum;
}

function valueNoise(x: number, y: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const tx = fade(x - x0);
  const ty = fade(y - y0);
  const top = lerp(hash(x0, y0, seed), hash(x0 + 1, y0, seed), tx);
  const bottom = lerp(hash(x0, y0 + 1, seed), hash(x0 + 1, y0 + 1, seed), tx);
  return lerp(top, bottom, ty);
}

/** Perlin系ノイズで使われる6t^5-15t^4+10t^3の補間曲線。 */
function fade(value: number): number {
  return value * value * value * (value * (value * 6 - 15) + 10);
}

function lerp(a: number, b: number, amount: number): number {
  return a + (b - a) * amount;
}

/** 32bit整数演算だけを使う決定的な座標hash。 */
function hash(x: number, y: number, seed: number): number {
  let value = Math.imul(x + 0x9e3779b9, 0x85ebca6b);
  value ^= Math.imul(y + 0xc2b2ae35, 0x27d4eb2d);
  value ^= seed | 0;
  value ^= value >>> 16;
  value = Math.imul(value, 0x7feb352d);
  value ^= value >>> 15;
  value = Math.imul(value, 0x846ca68b);
  value ^= value >>> 16;
  return (value >>> 0) / 0x100000000;
}

function hexToHsl(hex: number): Hsl {
  const r = ((hex >> 16) & 0xff) / 255;
  const g = ((hex >> 8) & 0xff) / 255;
  const b = (hex & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;
  if (delta === 0) return { h: 0, s: 0, l };

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (max === r) h = 60 * (((g - b) / delta) % 6);
  else if (max === g) h = 60 * ((b - r) / delta + 2);
  else h = 60 * ((r - g) / delta + 4);
  if (h < 0) h += 360;
  return { h, s, l };
}

function hslToRgb(hsl: Hsl): { r: number; g: number; b: number } {
  const c = (1 - Math.abs(2 * hsl.l - 1)) * hsl.s;
  const section = hsl.h / 60;
  const x = c * (1 - Math.abs((section % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (section < 1) [r, g] = [c, x];
  else if (section < 2) [r, g] = [x, c];
  else if (section < 3) [g, b] = [c, x];
  else if (section < 4) [g, b] = [x, c];
  else if (section < 5) [r, b] = [x, c];
  else [r, b] = [c, x];
  const m = hsl.l - c / 2;
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
